import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LiveReconnectingNotice from "../components/messages/LiveReconnectingNotice";
import ThreadList from "../components/messages/ThreadList";
import ThreadView from "../components/messages/ThreadView";
import PortalPageHeading from "../components/portal/PortalPageHeading";
import { useDirectThreadInPlace } from "../features/messages/hooks/useDirectThreadInPlace";
import { usePolling } from "../features/messages/hooks/usePolling";
import { useMessagesLive } from "../features/messages/live/useMessagesLive";
import { MessagesChannel } from "../features/messages/models/MessagesChannel";
import { selectThreads, selectThreadsStatus } from "../features/messages/selectors/messages-selectors";
import { fetchThreads } from "../features/messages/slice-extensions/messages-slice-extension";
import { usePortalLinkBuilder } from "../features/portal/hooks/usePortalLink";
import { usePortalPage } from "../features/portal/hooks/usePortalPage";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";

const NO_CHANNEL: MessagesChannel = { kind: "portal", memberId: "" };

/**
 * The member's conversations with the school: their direct thread and the threads of their Groups and
 * Sessions (the ward's when `?member=` names one, so a reload or a deep link opens that ward). Phone
 * first: the list, or one thread; from `lg` the list and a thread (the newest when none is picked) sit
 * side by side. Before anyone has written, the member's only (Direct) thread opens in place on the phone
 * with the empty drawing and the composer, so the first message needs no tap (US-92).
 */
const PortalMessagesPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { threadId } = useParams<{ threadId?: string }>();
  const { session, memberId, members, selectMember } = usePortalPage();
  const link = usePortalLinkBuilder();
  const channel: MessagesChannel = memberId ? { kind: "portal", memberId } : NO_CHANNEL;
  const threads = useAppSelector(selectThreads(channel));
  const status = useAppSelector(selectThreadsStatus);

  const signedIn = Boolean(session && memberId);
  const refresh = () => {
    if (memberId) dispatch(fetchThreads({ kind: "portal", memberId }));
  };
  const live = useMessagesLive(signedIn ? channel : null, { onUnreadChanged: refresh, onPresenceChanged: refresh, onResync: refresh });
  usePolling(refresh, signedIn && live !== "connected", memberId ?? "");
  const inPlace = useDirectThreadInPlace(threads, threadId);

  if (!session) return null;

  const active = threadId ? threads.find((thread) => thread.id === threadId) : threads[0];
  const threadLink = (id: string) => link(ROUTES.PORTAL_MESSAGE_THREAD.replace(":threadId", id));
  const showThread = Boolean(threadId) || inPlace.open;

  return (
    <section className="mx-auto min-h-[60vh] w-full max-w-3xl px-4 py-8">
      <PortalPageHeading
        title={t("messages.title")}
        members={members}
        memberId={memberId}
        onSelectMember={selectMember}
      />
      <LiveReconnectingNotice status={live} />
      {status === LoadingStatus.Failed && threads.length === 0 && (
        <p className="text-sm text-red-700 dark:text-red-400">{t("messages.loadFailed")}</p>
      )}
      <div className="lg:grid lg:min-h-96 lg:grid-cols-[14rem_1fr] lg:gap-8">
        <div className={showThread ? "hidden lg:block" : ""} data-testid="portal-thread-list-column">
          <ThreadList
            threads={threads}
            activeThreadId={active?.id}
            linkTo={(thread) => threadLink(thread.id)}
            loading={status === LoadingStatus.Loading}
          />
        </div>
        <div className={showThread ? "" : "hidden lg:block"} data-testid="portal-thread-column">
          {active && (
            <ThreadView
              key={active.id}
              channel={channel}
              thread={active}
              emptyText={
                active.kind === "Direct" ? t("portal.messages.empty", { school: active.title }) : t("messages.threadEmpty")
              }
              emptyTitle={
                active.kind === "Direct" ? t("portal.messages.emptyTitle", { school: active.title }) : undefined
              }
              backTo={inPlace.awaitingFirstMessage ? undefined : link(ROUTES.PORTAL_MESSAGES)}
              sessionLink={() => link(ROUTES.PORTAL)}
              paymentLink={link(ROUTES.PORTAL_MONEY)}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default PortalMessagesPage;
